// Pre-written captions, offered next to "Use a saved draft" on Compose.
//
// Deliberately constants and not a table. A saved draft belongs to a workspace
// and is the seam between the studio and Compose (ADR-0024); a template belongs
// to nobody, is the same for every workspace, and has no lifecycle — nothing
// creates, edits or deletes one at runtime. Putting it in the database would
// buy a migration, an RLS policy and a fetch on every Compose load in exchange
// for nothing the product needs.
//
// Two kinds of hole, and the brackets say which is which (see lib/tokens.ts):
//
//   [like this]     nobody can fill it for you. It is there to be noticed and
//                   written over, and it is obvious in a preview if it was not.
//   {{like_this}}   a brand detail. Compose fills it in from /captions when the
//                   caption is picked, so the booking link in a template is the
//                   booking link, and stays right when it changes.
//
// A template with no {{ }} in it would work; one with them is finished sooner.
//
// The closing line of most of these is a call to action written into the text
// on purpose: Facebook Page photo posts, Instagram feed posts and Reels have no
// CTA button available to a third-party publisher, so the caption is where the
// ask has to live. See docs/POST_COMPOSER_CAPABILITIES.md — which is also why
// those closing lines are the ones carrying {{booking}}.

export interface CaptionTemplate {
  key: string;
  /** What the dropdown shows. Short enough to read in a closed select. */
  label: string;
  /** The group it sits under, so twelve options do not read as a flat wall. */
  group: "Announce" | "Sell" | "Show" | "Ask";
  body: string;
}

export const CAPTION_TEMPLATES: CaptionTemplate[] = [
  {
    key: "new-service",
    label: "Something new",
    group: "Announce",
    body:
      "Something new at {{business}}: [what it is].\n\n" +
      "[One line on who it is for and what it does for them.]\n\n" +
      "Starts [date]. Book at {{booking}}, or call {{phone}}.",
  },
  {
    key: "event",
    label: "Event or class",
    group: "Announce",
    body:
      "[Event or class name] — [day], [date] at [time].\n\n" +
      "[Where it is.] [One line on what happens.]\n\n" +
      "[Number] places. Reserve one at {{booking}}.",
  },
  {
    key: "hours",
    label: "Opening hours change",
    group: "Announce",
    body:
      "Our hours are changing from [date].\n\n" +
      "[Days]: [times]\n[Days]: [times]\n\n" +
      "[Anything closed, and when it reopens.] Usual hours: {{hours}}.",
  },
  {
    key: "milestone",
    label: "Thank you / milestone",
    group: "Announce",
    body:
      "[Milestone — a year in business, a number of clients, an award].\n\n" +
      "Thank you to [who]. [One specific thing they made possible.]\n\n" +
      "[What is next.]",
  },
  {
    key: "offer",
    label: "Limited-time offer",
    group: "Sell",
    body:
      "[Offer] until [end date].\n\n" +
      "[What you get, in one line.] [What it normally costs, and what it costs now.]\n\n" +
      "Claim it at {{booking}}. Ends [end date] — after that it goes back to [normal price].",
  },
  {
    key: "booking",
    label: "Spaces still open",
    group: "Sell",
    body:
      "[Number] spaces left for [what] this [week/month].\n\n" +
      "[Who it suits.] [How long it takes.] [What it costs.]\n\n" +
      "Book at {{booking}}.",
  },
  {
    key: "testimonial",
    label: "Client words",
    group: "Show",
    body:
      "\"[The quote — their words, not tidied up.]\"\n" +
      "— [First name], [what they came in for]\n\n" +
      "[One line on what you did.]\n\n" +
      "Start the same way: {{booking}}.",
  },
  {
    key: "before-after",
    label: "Before and after",
    group: "Show",
    body:
      "[What it looked like at the start] → [what it looks like now].\n\n" +
      "[How long it took.] [What the work actually involved.]\n\n" +
      "[Who this would work for.] {{booking}}",
  },
  {
    key: "behind-scenes",
    label: "Behind the scenes",
    group: "Show",
    body:
      "[What is happening in this photo.]\n\n" +
      "[Why you do it this way — the bit customers never see.]\n\n" +
      "[Something small and true about the day.]",
  },
  {
    key: "team",
    label: "Meet the team",
    group: "Show",
    body:
      "Meet [name], our [role].\n\n" +
      "[How long they have been here.] [What they are best at.]\n\n" +
      "[One human detail.] Say hello when you are next in.",
  },
  {
    key: "tip",
    label: "Tip or how-to",
    group: "Ask",
    body:
      "[The tip, as a plain statement.]\n\n" +
      "1. [Step]\n2. [Step]\n3. [Step]\n\n" +
      "[Why it works.] [What to do if it does not.]",
  },
  {
    key: "question",
    label: "Question to the audience",
    group: "Ask",
    body:
      "[The question — one line, answerable in a few words.]\n\n" +
      "[Why you are asking, or your own answer.]\n\n" +
      "Tell us below.",
  },
];

/** Preserves the order above, so the groups appear in the order they are written. */
export function groupedTemplates(): Array<[string, CaptionTemplate[]]> {
  const groups: Array<[string, CaptionTemplate[]]> = [];
  for (const t of CAPTION_TEMPLATES) {
    const found = groups.find(([g]) => g === t.group);
    if (found) found[1].push(t);
    else groups.push([t.group, [t]]);
  }
  return groups;
}
